import { ApolloLink } from '@apollo/client';
import { DefinitionNode, OperationDefinitionNode } from 'graphql';

export type TrimMutationStringVariablesOptions = {
  excludedOperationNames?: string[];
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

export function trimStringLeafValues(value: unknown): unknown {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(trimStringLeafValues);
  if (!isPlainObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      trimStringLeafValues(entry),
    ])
  );
}

const isMutationOperation = (
  definitions: readonly DefinitionNode[],
  operationName: string
) => {
  const operations = definitions.filter(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition'
  );

  const operation = operationName
    ? operations.find((definition) => definition.name?.value === operationName)
    : operations[0];

  return operation?.operation === 'mutation';
};

export function createTrimMutationStringVariablesLink(
  options: TrimMutationStringVariablesOptions = {}
) {
  const excludedOperationNames = new Set(options.excludedOperationNames ?? []);

  return new ApolloLink((operation, forward) => {
    if (
      !operation.variables ||
      !isMutationOperation(
        operation.query.definitions,
        operation.operationName
      ) ||
      excludedOperationNames.has(operation.operationName)
    ) {
      return forward(operation);
    }

    operation.variables = trimStringLeafValues(
      operation.variables
    ) as typeof operation.variables;
    return forward(operation);
  });
}

export const trimMutationStringVariablesLink =
  createTrimMutationStringVariablesLink();
